import React, { useMemo } from 'react';

const API_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`;

/**
 * SVG Football Pitch Background
 * Vertical pitch (top-down view) with standard markings
 */
const PitchBackground = () => (
  <svg
    viewBox="0 0 680 1050"
    className="absolute inset-0 w-full h-full"
    preserveAspectRatio="xMidYMid meet"
  >
    {/* Grass background */}
    <rect width="680" height="1050" fill="#1a7a2e" />
    {/* Subtle grass stripes */}
    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
      <rect
        key={i}
        x="0"
        y={i * 105}
        width="680"
        height="105"
        fill={i % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'}
      />
    ))}
    {/* Outer boundary */}
    <rect x="40" y="40" width="600" height="970" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
    {/* Center line */}
    <line x1="40" y1="525" x2="640" y2="525" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
    {/* Center circle */}
    <circle cx="340" cy="525" r="91.5" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
    <circle cx="340" cy="525" r="4" fill="rgba(255,255,255,0.5)" />
    {/* Top penalty area */}
    <rect x="138" y="40" width="404" height="165" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
    {/* Top goal area */}
    <rect x="228" y="40" width="224" height="55" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
    {/* Top penalty spot */}
    <circle cx="340" cy="160" r="4" fill="rgba(255,255,255,0.5)" />
    {/* Top penalty arc */}
    <path d="M 268 205 A 91.5 91.5 0 0 0 412 205" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
    {/* Bottom penalty area */}
    <rect x="138" y="845" width="404" height="165" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
    {/* Bottom goal area */}
    <rect x="228" y="955" width="224" height="55" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
    {/* Bottom penalty spot */}
    <circle cx="340" cy="890" r="4" fill="rgba(255,255,255,0.5)" />
    {/* Bottom penalty arc */}
    <path d="M 268 845 A 91.5 91.5 0 0 1 412 845" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
    {/* Corner arcs */}
    <path d="M 40 50 A 10 10 0 0 0 50 40" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
    <path d="M 630 40 A 10 10 0 0 0 640 50" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
    <path d="M 40 1000 A 10 10 0 0 1 50 1010" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
    <path d="M 630 1010 A 10 10 0 0 1 640 1000" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
  </svg>
);

/**
 * Player node: photo circle + jersey badge + name
 */
const PlayerNode = ({ player, x, y, isCaptain, teamColor }) => {
  const photoUrl = player.player?.photo_url
    ? `${API_URL}${player.player.photo_url}`
    : '/images/avatars/default-player.png';
  const jersey = player.jersey_number || '?';
  const lastName = player.player?.last_name || '?';

  return (
    <div
      className="absolute flex flex-col items-center"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        transform: 'translate(-50%, -50%)',
        animation: 'fadeInUp 0.5s ease-out both',
        animationDelay: `${Math.random() * 0.3}s`,
      }}
    >
      {/* Photo circle */}
      <div className="relative">
        <div
          className="w-10 h-10 rounded-full border-2 overflow-hidden flex items-center justify-center text-white font-bold text-base shadow-lg"
          style={{
            borderColor: teamColor,
            backgroundColor: 'transparent',
          }}
        >
          <img
            src={photoUrl}
            alt={lastName}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentNode.style.backgroundColor = teamColor;
              e.target.parentNode.innerHTML = `<span class="text-white font-bold text-base">${jersey}</span>`;
            }}
          />
        </div>
        {/* Jersey number badge */}
        <div
          className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold shadow-md"
          style={{ backgroundColor: teamColor, color: '#fff' }}
        >
          {jersey}
        </div>
        {/* Captain badge */}
        {isCaptain && (
          <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-400 flex items-center justify-center text-[8px] font-bold text-black shadow-md">
            C
          </div>
        )}
      </div>
      {/* Player name */}
      <div
        className="mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold text-white whitespace-nowrap max-w-[80px] truncate"
        style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
      >
        {lastName}
      </div>
    </div>
  );
};

/**
 * Parse formation string like "4-4-2" → [4, 4, 2]
 */
const parseFormation = (str) => {
  if (!str) return [4, 4, 2];
  const parts = str.split('-').map(Number).filter(n => !isNaN(n) && n > 0);
  return parts.length > 0 ? parts : [4, 4, 2];
};

/**
 * Distribute players into rows based on formation.
 * GK is always separated. Outfield players ordered by position_id then jersey.
 * Fill rows sequentially from back to front with formation counts.
 */
const layoutPlayers = (players, formationStr, isHome) => {
  const formation = parseFormation(formationStr);

  // Separate GK and outfield
  const gk = players.find(p => p.position?.name === 'GK');
  const outfield = players
    .filter(p => p.position?.name !== 'GK')
    .sort((a, b) => {
      const posOrder = (a.position_id || 0) - (b.position_id || 0);
      if (posOrder !== 0) return posOrder;
      return (a.jersey_number || 0) - (b.jersey_number || 0);
    });

  // Build rows from formation counts
  const rows = [];
  let idx = 0;
  for (const count of formation) {
    rows.push(outfield.slice(idx, idx + count));
    idx += count;
  }
  // Any remaining players go in the last row
  if (idx < outfield.length) {
    const last = rows[rows.length - 1] || [];
    rows[rows.length - 1] = [...last, ...outfield.slice(idx)];
  }

  // Calculate positions as percentages
  const result = [];

  // GK position
  if (gk) {
    result.push({
      player: gk,
      x: 50,
      y: isHome ? 92 : 8,
    });
  }

  // Outfield rows
  const rowCount = rows.length;
  for (let rowIdx = 0; rowIdx < rowCount; rowIdx++) {
    const row = rows[rowIdx];
    // Y: distribute rows in the team's half
    // Home: from 82% (defenders) to 60% (forwards)
    // Away: from 18% (defenders) to 40% (forwards)
    // 20% gap at center to prevent player overlap
    let y;
    if (rowCount === 1) {
      y = isHome ? 68 : 32;
    } else {
      const t = rowIdx / (rowCount - 1);
      y = isHome
        ? 82 - t * 22   // 82 → 60
        : 18 + t * 22;  // 18 → 40
    }

    const n = row.length;
    for (let i = 0; i < n; i++) {
      // X: distribute evenly with margins
      let x;
      if (n === 1) {
        x = 50;
      } else {
        x = 14 + i * (72 / (n - 1));
      }

      result.push({
        player: row[i],
        x,
        y,
      });
    }
  }

  return result;
};

/**
 * FormationsOverlay
 * Shows both team lineups on a football pitch
 */
const FormationsOverlay = ({ wsData }) => {
  const match = wsData?.match;

  const { homePlayers, awayPlayers } = useMemo(() => {
    if (!match?.player_positions) return { homePlayers: [], awayPlayers: [] };

    const homeId = match.home_team_id;
    const awayId = match.away_team_id;

    return {
      homePlayers: match.player_positions.filter(p => p.team_id === homeId || p.team?.id === homeId),
      awayPlayers: match.player_positions.filter(p => p.team_id === awayId || p.team?.id === awayId),
    };
  }, [match]);

  const homeLayout = useMemo(
    () => layoutPlayers(homePlayers, match?.home_formation, true),
    [homePlayers, match?.home_formation]
  );

  const awayLayout = useMemo(
    () => layoutPlayers(awayPlayers, match?.away_formation, false),
    [awayPlayers, match?.away_formation]
  );

  const homeFormation = match?.home_formation || '4-4-2';
  const awayFormation = match?.away_formation || '4-4-2';
  const homeTeamName = match?.home_team?.short_name || match?.home_team?.name || 'Home';
  const awayTeamName = match?.away_team?.short_name || match?.away_team?.name || 'Away';

  return (
    <div className="w-full h-full flex items-center justify-center bg-[#1a7a2e]" style={{ animation: 'fadeIn 0.6s ease-out' }}>
      <div className="relative mx-auto" style={{ aspectRatio: '680 / 1050', maxHeight: '95vh', height: '95vh' }}>
        {/* Pitch */}
        <PitchBackground />

        {/* Away team label (top) */}
        <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
          <div className="bg-blue-600/90 text-white px-3 py-1 rounded text-sm font-bold shadow-lg">
            {awayTeamName} — {awayFormation}
          </div>
        </div>

        {/* Home team label (bottom) */}
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
          <div className="bg-red-600/90 text-white px-3 py-1 rounded text-sm font-bold shadow-lg">
            {homeTeamName} — {homeFormation}
          </div>
        </div>

        {/* Away players (top half) */}
        {awayLayout.map(({ player, x, y }) => (
          <PlayerNode
            key={`away-${player.id}`}
            player={player}
            x={x}
            y={y}
            isCaptain={player.is_captain}
            teamColor="#2563eb"
          />
        ))}

        {/* Home players (bottom half) */}
        {homeLayout.map(({ player, x, y }) => (
          <PlayerNode
            key={`home-${player.id}`}
            player={player}
            x={x}
            y={y}
            isCaptain={player.is_captain}
            teamColor="#dc2626"
          />
        ))}
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translate(-50%, -40%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }
      `}</style>
    </div>
  );
};

export default FormationsOverlay;
